// src/stores/blogConfigStore.js
//
// Blog content configuration.
// Reads from publicConfig/blogConfig via onSnapshot (authenticated) or
// HTTP Cloud Function fallback (unauthenticated visitors blocked by App Check).

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { doc, setDoc, serverTimestamp } from 'firebase/firestore'
import { onSnapshot } from '@/services/sessionListeners'
import { db } from '@/services/firebaseconfig'

const DEFAULTS = {
  categories: [
    { id: 1, name: 'Solar Energy Simplified' },
    { id: 2, name: 'Solar Energy Revolution' },
    { id: 3, name: 'Essential Solar Devices' },
    { id: 4, name: 'Benefits of Solar Power' },
    { id: 5, name: 'Energy Independence' },
    { id: 6, name: 'Sustainable Solar Solutions' },
  ],
  posts: [],
}

export const useBlogConfigStore = defineStore('blogConfig', () => {
  const config = ref({ ...DEFAULTS })
  const loaded = ref(false)
  let _unsubscribe = null

  // ── Public Getters ────────────────────────────────────────────────────────

  const categories = computed(() => config.value.categories || [])

  const allPosts = computed(() =>
    (config.value.posts || []).sort((a, b) => (b.date || '').localeCompare(a.date || ''))
  )

  const publishedPosts = computed(() => allPosts.value.filter(p => p.published !== false))

  function getPostsByCategory(categoryId) {
    if (!categoryId || categoryId === 0) return publishedPosts.value
    return publishedPosts.value.filter(p => p.categoryId === categoryId)
  }

  function getPostById(id) {
    return allPosts.value.find(p => p.id === id || p.id === String(id)) || null
  }

  /** The public site's lookup: an unpublished post is not found (Settings keeps using getPostById). */
  function getPublishedPostById(id) {
    return publishedPosts.value.find(p => p.id === id || p.id === String(id)) || null
  }

  // ── Subscription (no auth, public doc) ────────────────────────────────────

  function subscribe() {
    if (_unsubscribe) return

    let resolved = false
    const docRef = doc(db, 'publicConfig', 'blogConfig')

    // Race: HTTP fetch immediately
    import('@/utils/publicConfigLoader')
      .then(({ fetchPublicConfig }) => {
        fetchPublicConfig(['blogConfig'])
          .then(data => {
            if (!resolved && data?.blogConfig) {
              resolved = true
              config.value = {
                categories: data.blogConfig.categories || DEFAULTS.categories,
                posts: data.blogConfig.posts || DEFAULTS.posts,
              }
              loaded.value = true
            }
          })
          .catch(() => {})
      })
      .catch(() => {})

    // Also try onSnapshot
    _unsubscribe = onSnapshot(
      docRef,
      snap => {
        if (snap.exists()) {
          resolved = true
          const data = snap.data()
          config.value = {
            categories: data.categories || DEFAULTS.categories,
            posts: data.posts || DEFAULTS.posts,
          }
        }
        loaded.value = true
      },
      () => {}
    )
  }

  function unsubscribe() {
    if (_unsubscribe) {
      _unsubscribe()
      _unsubscribe = null
    }
    loaded.value = false
  }

  // ── Admin Save ────────────────────────────────────────────────────────────

  async function saveConfig(newConfig, userId) {
    const docRef = doc(db, 'publicConfig', 'blogConfig')
    await setDoc(docRef, {
      ...newConfig,
      _metadata: {
        updatedBy: userId || 'unknown',
        updatedAt: serverTimestamp(),
      },
    })
  }

  return {
    config,
    loaded,
    categories,
    allPosts,
    publishedPosts,
    getPostsByCategory,
    getPostById,
    getPublishedPostById,
    subscribe,
    unsubscribe,
    saveConfig,
  }
})
